import { CheckCircle2, Loader2, MailX, TriangleAlert } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { memberRewardsApi } from '../services/memberRewardsApi';

type Phase = 'confirm' | 'invalid' | 'done';

/**
 * Unsubscribe page for member invite emails (docs/prd-member-rewards.md §6.1).
 * No login: the opaque token in the link is the authorization, and it can only
 * opt the address out. The email link lands on the API first, which redirects
 * here; one-click mail clients POST to the API directly.
 */
export default function UnsubscribeInvites() {
  const { t } = useTranslation('rewards');
  const [searchParams] = useSearchParams();
  // Read once; the token is scrubbed from the address bar below.
  const [token] = useState(() => searchParams.get('token') || '');
  const [phase, setPhase] = useState<Phase>(token ? 'confirm' : 'invalid');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Keep the token out of browser history and outgoing referrers.
  useEffect(() => {
    if (!token) return;
    window.history.replaceState(window.history.state, '', window.location.pathname);
  }, [token]);

  const unsubscribe = async () => {
    setBusy(true);
    setError('');
    try {
      await memberRewardsApi.unsubscribeInvites(token);
      setPhase('done');
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'INVALID_TOKEN') setPhase('invalid');
      else setError(t('unsubscribe.error'));
    } finally {
      setBusy(false);
    }
  };

  const shell = (icon: ReactNode, title: string, body: string, actions?: ReactNode) => (
    <div className="min-h-screen flex items-center justify-center bg-warm-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-brand p-8 text-center space-y-4">
          <div className="flex justify-center">{icon}</div>
          <h1 className="text-h3 font-bold text-charcoal">{title}</h1>
          <p className="text-body text-charcoal/70">{body}</p>
          {error && <p className="text-body text-red-600">{error}</p>}
          {actions && <div className="pt-2 space-y-3">{actions}</div>}
        </div>
      </div>
    </div>
  );

  const browse = (
    <Link
      to="/activities"
      className="block font-semibold text-primary transition-colors hover:text-primary/80"
    >
      {t('unsubscribe.browseCta')}
    </Link>
  );

  if (phase === 'invalid') {
    return shell(
      <TriangleAlert className="h-10 w-10 text-amber-500" />,
      t('unsubscribe.invalidTitle'),
      t('unsubscribe.invalidBody'),
      browse
    );
  }

  if (phase === 'done') {
    return shell(
      <CheckCircle2 className="h-10 w-10 text-primary" />,
      t('unsubscribe.doneTitle'),
      t('unsubscribe.doneBody'),
      browse
    );
  }

  return shell(
    <MailX className="h-10 w-10 text-primary" />,
    t('unsubscribe.title'),
    t('unsubscribe.body'),
    <button
      type="button"
      onClick={unsubscribe}
      disabled={busy}
      className="w-full inline-flex items-center justify-center rounded-lg bg-primary px-6 py-3 font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
    >
      {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {t('unsubscribe.cta')}
    </button>
  );
}
